import type { User } from '@entities/user';
import type { BaseUsecase } from '@usecases/base-usecase';

export interface UserLoginRequest {
  email: string;
  password: string;
}

export function isAxiosLikeError(error: unknown): error is HttpError {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    ('status' in error ||
      ('response' in error &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof (error as any).response?.status === 'number'))
  ) {
    return true;
  }

  return false;
}

export interface HttpError extends Error {
  status?: number;
  code?: string;
  response?: {
    status: number;
    data?: unknown;
  };
  originalError?: unknown;
}

export class InvalidUserLoginRequest extends Error {
  override name = 'InvalidUserLoginRequest';
  public kind = 'INVALID_USER_LOGIN_REQUEST'; // <── ADD KIND

  constructor(
    public override readonly message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    // Optionally capture original stack, etc.
    Object.setPrototypeOf(this, InvalidUserLoginRequest.prototype);
  }
}

export class MissingIntegrationOrNumberNotAssign extends Error {
  override name = 'MissingIntegrationOrNumberNotAssign';
  public kind = 'MISSING_INTEGRATION_OR_NUMBER_NOT_ASSIGN'; // <── ADD KIND

  constructor(
    public override readonly message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    Object.setPrototypeOf(this, MissingIntegrationOrNumberNotAssign.prototype);
  }
}

export interface UserLoginRepository {
  addUserDetails(user: User): Promise<void>;
}

export interface UserLoginAuthenticator {
  authenticate(credentials: UserLoginRequest): Promise<User>;
}

export interface UserLoginEventPublisher {
  publish(user: User): Promise<void>;
}

export type UserLoginUsecase = BaseUsecase<UserLoginRequest, void>;

export class UserLogin implements UserLoginUsecase {
  constructor(
    private authenticator: UserLoginAuthenticator,
    private repository: UserLoginRepository,
    private publisher: UserLoginEventPublisher,
  ) {}

  async run(req: UserLoginRequest): Promise<void> {
    try {
      const user = await this.authenticator.authenticate(req);
      await Promise.all([
        this.repository.addUserDetails(user),
        this.publisher.publish(user),
      ]);
    } catch (error: any) {
      console.log(
        'Error inside UserLoginEventPublisher details ',
        JSON.stringify(error),
      );
      if (error?.name === 'MissingIntegrationOrNumberNotAssign') {
        throw new MissingIntegrationOrNumberNotAssign(
          'No active integration found or phone number not linked to one.',
          error,
        );
      }
      if (isAxiosLikeError(error)) {
        const status = error.response?.status ?? error.status;
        if (status === 500) {
          throw new MissingIntegrationOrNumberNotAssign(
            'No active integration found or phone number not linked to one.',
            error,
          );
        }

        // You could handle other status codes here (e.g., 401, 403, etc.)
      }
      throw new InvalidUserLoginRequest('Invalid email or password', error);
    }
  }
}
